import React, { useEffect, useState } from 'react';
import { toast } from '../../../utils/toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Alert,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { facturasApi } from '../../../services/api/facturasApi';
import { ConfiguracionFactura } from './types/facturas.types';
import './configuracionFacturasModal.scss';

interface Props {
  open: boolean;
  onClose: () => void;
  initialData?: ConfiguracionFactura;
  recargarDatos: () => void;
}

const ConfiguracionFacturasModal: React.FC<Props> = ({ 
  open, 
  onClose, 
  initialData,
  recargarDatos 
}) => {
  const [loading, setLoading] = useState(false);
  const [facturasDisponibles, setFacturasDisponibles] = useState<number>(0);
  
  const { 
    register, 
    handleSubmit, 
    setValue, 
    watch,
    formState: { errors },
    reset 
  } = useForm<ConfiguracionFactura>();

  const numeroInicio = watch('NumeroInicio');
  const numeroFin = watch('NumeroFin');
  const numeroActual = watch('NumeroActual');

  useEffect(() => {
    if (open) {
      if (initialData) {
        setValue('Prefijo', initialData.Prefijo || '');
        setValue('NumeroInicio', initialData.NumeroInicio);
        setValue('NumeroFin', initialData.NumeroFin);
        setValue('NumeroActual', initialData.NumeroActual);
        calcularDisponibles(initialData.NumeroFin, initialData.NumeroActual);
      } else {
        reset({
          Prefijo: '',
          NumeroInicio: 1,
          NumeroFin: 100,
          NumeroActual: 1
        });
        calcularDisponibles(100, 1);
      }
    }
  }, [open, initialData]);

  useEffect(() => {
    if (numeroFin && numeroActual) {
      calcularDisponibles(numeroFin, numeroActual);
    }
  }, [numeroFin, numeroActual]);

  const calcularDisponibles = (fin: number, actual: number) => {
    const disponibles = fin - actual + 1;
    setFacturasDisponibles(disponibles > 0 ? disponibles : 0);
  };

  const onSubmit = async (data: ConfiguracionFactura) => {
    try {
      setLoading(true);
      await facturasApi.actualizarConfiguracion(data);
      
      await Swal.fire({
        title: 'Éxito',
        text: 'Configuración guardada correctamente',
        icon: 'success',
        confirmButtonColor: '#9b59b6',
      });
      
      recargarDatos();
      onClose();
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      className="facturas-modal"
      PaperProps={{
        sx: {
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle className="modal-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" fontWeight={700}>
            {initialData ? 'Editar Configuración' : 'Nueva Configuración'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent className="modal-content">
        <Box className="disponibles-alert" mb={3}>
          <Alert 
            severity={facturasDisponibles <= 30 ? "warning" : "info"}
            icon={false}
          >
            <Typography variant="body2" fontWeight={600}>
              Facturas disponibles: <span className="count">{facturasDisponibles}</span>
            </Typography>
          </Alert>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} className="facturas-form">
          <TextField
            label="Prefijo (opcional)"
            fullWidth
            margin="normal"
            {...register('Prefijo')}
            placeholder="Ej: FAC-"
            className="form-field"
          />

          <TextField
            label="Número Inicial"
            type="number"
            fullWidth
            margin="normal"
            {...register('NumeroInicio', {
              required: 'Este campo es requerido',
              min: { value: 1, message: 'El número debe ser mayor a 0' },
              valueAsNumber: true
            })}
            error={!!errors.NumeroInicio}
            helperText={errors.NumeroInicio?.message}
            className="form-field"
          />

          <TextField
            label="Número Final"
            type="number"
            fullWidth
            margin="normal"
            {...register('NumeroFin', {
              required: 'Este campo es requerido',
              min: { value: 1, message: 'El número debe ser mayor a 0' },
              validate: (value) => {
                if (numeroInicio && value < numeroInicio) {
                  return 'El número final debe ser mayor al inicial';
                }
                return true;
              },
              valueAsNumber: true
            })}
            error={!!errors.NumeroFin}
            helperText={errors.NumeroFin?.message}
            className="form-field"
          />

          <TextField
            label="Número Actual"
            type="number"
            fullWidth
            margin="normal"
            {...register('NumeroActual', {
              required: 'Este campo es requerido',
              min: { value: 1, message: 'El número debe ser mayor a 0' },
              validate: (value) => {
                if (numeroInicio && value < numeroInicio) {
                  return 'El número actual debe ser mayor o igual al inicial';
                }
                if (numeroFin && value > numeroFin) {
                  return 'El número actual no puede ser mayor al final';
                }
                return true;
              },
              valueAsNumber: true
            })}
            error={!!errors.NumeroActual}
            helperText={errors.NumeroActual?.message}
            className="form-field"
          />
        </form>
      </DialogContent>

      <Divider />

      <DialogActions className="modal-actions">
        <Button onClick={handleClose} className="cancel-button">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={<SaveIcon />}
          className="save-button"
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfiguracionFacturasModal;